//#region Imports
import React, { useEffect, useLayoutEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
//MUI
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { Backdrop, Box, Button, Grid, MenuItem, Select, Typography, useMediaQuery } from '@mui/material';
import TextField from '../../../components/SanitizedTextField/SanitizedTextField';
import AppLoader from '../../../components/AppLoader/AppLoader';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { tableStyleOverDue } from '../../../style/common';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import crossIcon from "../../../images/remove.png";
import pdfIcon from "../../../images/pdfIcon.png";

//components
//scss
import "../../../style/common.scss";
import "./PartPaymentWorkflowDialog.scss";
import {
    WORKFLOW_MOBILE_MEDIA,
    workflowDialogClassName,
    workflowDialogShellProps,
} from '../../../utils/workflowDialogShell';
//Yup
import * as Yup from "yup";
//formik

import { useFormik } from 'formik';
//urls
import { cfpBaseUrl } from '../../../network/urls';
// use scan detection
import useScanDetection from 'use-scan-detection';
import CheckPermissions from '../../../authorization/authorization';
//Services
import StepProgressBar from '../../../components/StepProgressBar/StepProgressBar';
import ProcessingSummaryTableBilling from '../../../components/processingSummaryTable/processingSummaryTableBilling';
import { tableStyle } from '../../../style/common';

import { useDispatch, useSelector } from 'react-redux';
import BasicTable from '../../../components/basicTable/basicTable';
import ServiceDashboardTables from '../../../components/ServiceDashboardTables/ServiceDashboardTables';
import Error from '../../../components/error/error';
import { SearchIndividualApplication } from '../../../network/APIs/APIs';
import { addBusinessDays, calculateRemainingBusinessDays, dBillAPI, daysBetweenDatesExcludingWeekendsAndHolidays, downloadBill, getDocumentBackGroundColor, normalizeDocumentDataUrl, numberFormatting, subdivisionName } from '../../../services/Functions';
import tickicon from "../../../images/Accept.png";
import {
    fetchPaginatedPartPaymentNormal,
    filterDateFrom as filterDateFromNormal,
    filterDateTo as filterDateToNormal,
    filterObjectionStatus as filterObjectionStatusNormal,
    filterProgressId as filterProgressIdNormal,
    firstPage as firstPageNormal,
    lastPage as lastPageNormal,
    nextPage as nextPageNormal,
    previousPage as previousPageNormal,
    resetDateFilter as resetDateFilterNormal,
    resetObjectionStatusFilter as resetObjectionStatusFilterNormal,
    resetProgressIdFilter as resetProgressIdFilterNormal
} from '../../../features/billingAndCollection/partPaymentNormalSlice';
import {
    fetchPaginatedPartPaymentOverdue,
    filterDateFrom as filterDateFromOverdue,
    filterDateTo as filterDateToOverdue,
    filterObjectionStatus as filterObjectionStatusOverdue,
    filterProgressId as filterProgressIdOverdue,
    firstPage as firstPageOverdue,
    lastPage as lastPageOverdue,
    nextPage as nextPageOverdue,
    previousPage as previousPageOverdue,
    resetDateFilter as resetDateFilterOverdue,
    resetObjectionStatusFilter as resetObjectionStatusFilterOverdue,
    resetProgressIdFilter as resetProgressIdFilterOverdue
} from '../../../features/billingAndCollection/partPaymentOverdueSlice';
import Base64Image from '../../../components/Base64Image/Base64Image';
import OpenBase64ImageInPopup from '../../../services/OpenBase64ImageInPopup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { getInitialAdminOfficeCode } from '../../../utils/adminCompany';
import { DISCO_COMPANIES } from '../../../constants/discoCompanies';
//#endregion

const PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.min.mjs';
const PDFJS_WORKER_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.worker.min.mjs';

const loadPdfJsLib = () => new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
        resolve(window.pdfjsLib);
        return;
    }

    const existing = document.querySelector('script[data-pdfjs-lib]');
    if (existing) {
        existing.addEventListener('load', () => setTimeout(() => resolve(window.pdfjsLib), 100));
        existing.addEventListener('error', reject);
        return;
    }

    const script = document.createElement('script');
    script.src = PDFJS_CDN;
    script.type = 'module';
    script.dataset.pdfjsLib = 'true';
    script.onload = () => setTimeout(() => resolve(window.pdfjsLib), 100);
    script.onerror = reject;
    document.head.appendChild(script);
});

const isPdfDataUrl = (src) => {
    const normalized = normalizeDocumentDataUrl(src);
    if (!normalized) return false;
    return normalized.startsWith('data:application/pdf') || normalized.slice(0, 6) === 'data:a';
};

const isImageDataUrl = (src) => {
    const normalized = normalizeDocumentDataUrl(src);
    if (!normalized) return false;
    return normalized.startsWith('data:image') || normalized.slice(0, 6) === 'data:i';
};

const DocumentPreviewThumb = ({ src, width = '80', height = '50' }) => {
    if (!src) return null;
    if (isPdfDataUrl(src)) {
        return <Base64Image data={pdfIcon} width="40" />;
    }
    if (isImageDataUrl(src)) {
        return (
            <Base64Image
                data={normalizeDocumentDataUrl(src)}
                width={width}
                height={height}
            />
        );
    }
    return <Base64Image data={pdfIcon} width="40" />;
};

const pdfToImage = async (input) => {
    const normalized = normalizeDocumentDataUrl(input);
    if (!normalized) return '';

    if (!isPdfDataUrl(normalized)) {
        return normalized;
    }

    const pdfjsLib = await loadPdfJsLib();
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;

    const base64Body = normalized.includes(',')
        ? normalized.split(',')[1]
        : normalized.replace(/^data:application\/pdf;base64,/, '');

    const binary = atob(base64Body);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }

    const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.height = viewport.height;
    canvas.width = viewport.width;

    await page.render({ canvasContext: context, viewport }).promise;
    return canvas.toDataURL('image/png');
};

const AmendBill2636Table = forwardRef((props, ref) => {

    //#region initial values
    const initialValues = {
        iv_approved_no_of_days: null,
        iv_approved_due_date: null,
        claimedPaymentAmount: "",
    }
    //#endregion

    //#region get item local storage
    const { token, code: officeCode, hierarchy: officeHierarchy, userId, permissions, selectedAdminCompanyCode } = useSelector((state) => state.auth);
    const isWorkflowMobile = useMediaQuery(WORKFLOW_MOBILE_MEDIA);
    const [selectedOfficeCode, setSelectedOfficeCode] = useState(
        getInitialAdminOfficeCode(officeCode, selectedAdminCompanyCode),
    );
    const apiOfficeCode = selectedOfficeCode || officeCode;

    useEffect(() => {
        if (String(officeCode) === "10" && selectedAdminCompanyCode) {
            setSelectedOfficeCode(String(selectedAdminCompanyCode));
        }
    }, [officeCode, selectedAdminCompanyCode]);
    const subDivisions = JSON.parse(window.localStorage.getItem('subDivisions'));
    const navigate = useNavigate();
    //#endregion

    //#region states

    const [error, setError] = useState()
    const [ddeApplicationsNormal, setDdeApplicationsNormal] = useState([]);
    const [ddeApplicationsNormalToDataGrid, setDdeApplicationsNormalToDataGrid] = useState([]);
    const [dayName, setDayName] = useState('-');
    const [ddeApplicationsOverdue, setDdeApplicationsOverdue] = useState([]);
    const [ddeApplicationsOverdueToDataGrid, setDdeApplicationsOverdueToDataGrid] = useState([]);
    const [open, setOpen] = React.useState(false);
    const [dialogRowData, setDialogRowData] = React.useState({});
    const [applicationId, setapllicationid] = React.useState('')
    const [eFileData, setEFileData] = useState([]);
    const [billAdjustmentDocuments, setBillAdjustmentDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [viewBackdrop, setViewBackdrop] = useState(false);
    const [applicationSubittedPopUp, setApplicationSubmittedPopUp] = useState(false);
    const [submittedDocViewer, setSubmittedDocViewer] = useState({ open: false, image: '', name: '' });
    const [submittedCnicFrontPreview, setSubmittedCnicFrontPreview] = useState('');
    const [submittedCnicBackPreview, setSubmittedCnicBackPreview] = useState('');
    const [availableDays, setAvailableDays] = useState([]);
    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [actionTakenPopup, setActionTakenPopup] = useState(false);

    const [approvedPayment, setApprovedPayment] = useState("");
    const [approvedPaymentError, setApprovedPaymentError] = useState("");

    const [flag_applicantCNICFront, setFlag_applicantCNICFront] = useState(null);
    const [flag_applicantCNICBack, setFlag_applicantCNICBack] = useState(null);
    const [flag_paidBillImageFlag, setFlag_paidBillImageFlag] = useState(null);
    const [
        applicantCNICBackObjectionRemarks,
        setApplicantCNICBackObjectionRemarks,
    ] = useState("");
    const [documnets, setDocuments] = useState({})
    const [apiPopup, setApiPopup] = useState({ open: false, type: '', message: '' });
    const [resultPopup, setResultPopup] = useState(null);
    const tableColumnsProcessingSummary = [
        {
            header: 'Step',
            accessorKey: 'stepName',
        },
        {
            header: 'Processed By',
            accessorKey: 'processedBy',
        },
        {
            header: 'Date',
            accessorKey: 'date',
        },
        {
            header: 'Remarks',
            accessorKey: 'remarks',
        },
    ]

    const [tableDataProcessingSummary, setTableDataProcessingSummary] = useState([
        {
            stepName: 'Application Submission',
            processedBy: 'Akram',
            date: '24-Dec-2023',
            time: '02:23:19 AM',
            remarks: '-',
        },
        {
            stepName: 'Application Submission',
            processedBy: 'Akram',
            date: '24-Dec-2023',
            time: '02:23:19 AM',
            remarks: '-',
        },
        {
            stepName: 'Application Submission',
            processedBy: 'Akram',
            date: '24-Dec-2023',
            time: '02:23:19 AM',
            remarks: '-',
        },
    ])
    const dispatch = useDispatch();
    let DDStatusBarArray = [
        {
            title1: 'APPLICATION',
            title2: 'SUBMITTED',
        },
        {
            title1: 'ACTION',
            title2: 'TAKEN',
        },

    ]

    const [foundInDDE, setFoundInDDE] = useState();
    const { state } = useLocation();
    const { foundId } = state ? state : '';
    const [isReleventToUser, setIsRelevantToUser] = useState(false)
    const [
        applicantCNICFrontObjectionRemarks,
        setApplicantCNICFrontObjectionRemarks,
    ] = useState("");
    const [
        paidBillImageObjectionRemarks,
        setPaidBillImageObjectionRemarks,
    ] = useState("");
    const [copyOfApplicantCNICVerifyPopUp, setCopyOfApplicantCNICVerifyPopUp] =
        useState(false);
    const [paidBillImagePopUp, setPaidBillImagePopUp] = useState(false);
    const [owenershipDocumentVerifyPopUp, setOwenershipDocumentVerifyPopUp] =
        useState(false);
    //#endregion

    //#endregion

    const { currentPage: currentPageNormal, data: dataNormal, pageSize: pageSizeNormal, totalPages: totalPagesNormal, totalRecords: totalRecordsNormal, dateFrom: dateFromNormal, dateTo: dateToNormal, applicationProgressId: applicationProgressIdNormal, objectionStatus: objectionStatusNormal, error: errorNormal, isLoading: isLoad } = useSelector((state) => state.partPaymentNormalPaginated);
    const { currentPage: currentPageOverdue, data: dataOverdue, pageSize: pageSizeOverdue, totalPages: totalPagesOverdue, totalRecords: totalRecordsOverdue, dateFrom: dateFromOverdue, dateTo: dateToOverdue, applicationProgressId: applicationProgressIdOverdue, objectionStatus: objectionStatusOverdue, error: errorOverdue, isLoading: isLoadd } = useSelector((state) => state.partPaymentOverduePaginated);

    const handleNextPage = () => {
        dispatch(nextPageNormal());
    };

    const handleDateFromFilter = (value) => {
        if (value) {
            setViewBackdrop(true)
            dispatch(filterDateFromNormal(value))
        }
    }

    const handleDateToFilter = (value) => {
        if (value) {
            setViewBackdrop(true)
            dispatch(filterDateToNormal(value))
        }
    }

    const handleResetDateFilter = () => {
        dispatch(resetDateFilterNormal())
    }

    const handleProgressIdFilter = (value) => {
        if (value || value === 0) {
            setViewBackdrop(true)
            dispatch(filterProgressIdNormal(value))
        }
    }

    const handleResetProgressIdFilter = () => {
        dispatch(resetProgressIdFilterNormal())
    }

    const handleObjectionStatusFilter = (value) => {
        if (value || value === 0) {
            setViewBackdrop(true)
            dispatch(filterObjectionStatusNormal(value))
        }
    }

    const handleResetObjectionStatusFilter = () => {
        dispatch(resetObjectionStatusFilterNormal())
    }

    const handlePreviousPage = () => {
        dispatch(previousPageNormal());
    };

    const handleFirstPage = () => {
        dispatch(firstPageNormal());
    };

    const handleLastPage = (page) => {
      dispatch(lastPageNormal(typeof page === 'number' ? page : totalPagesNormal));
    };

    useEffect(() => {
        if ((props.showTable && currentPath == "/amend-2636-bill-table") && token) {
            dispatch(fetchPaginatedPartPaymentNormal({ page: currentPageNormal, pageSize: pageSizeNormal, dateFrom: dateFromNormal, dateTo: dateToNormal, applicationProgressId: applicationProgressIdNormal, objectionStatus: objectionStatusNormal, officeCode: apiOfficeCode }));
        }
    }, [currentPageNormal, pageSizeNormal, dateFromNormal, dateToNormal, applicationProgressIdNormal, objectionStatusNormal, apiOfficeCode]);



    useEffect(() => {
        if (dataNormal) {
            setDdeApplicationsNormal(dataNormal)
            setViewBackdrop(false)
        }
    }, [dataNormal])

    const handleNextPageOverdue = () => {
        dispatch(nextPageOverdue());
    };

    const handleDateFromFilterOverdue = (value) => {
        if (value) {
            setViewBackdrop(true)
            dispatch(filterDateFromOverdue(value))
        }
    }

    const handleDateToFilterOverdue = (value) => {
        if (value) {
            setViewBackdrop(true)
            dispatch(filterDateToOverdue(value))
        }
    }

    const handleResetDateFilterOverdue = () => {
        dispatch(resetDateFilterOverdue())
    }

    const handleProgressIdFilterOverdue = (value) => {
        if (value || value === 0) {
            setViewBackdrop(true)
            dispatch(filterProgressIdOverdue(value))
        }
    }

    const handleResetProgressIdFilterOverdue = () => {
        dispatch(resetProgressIdFilterOverdue())
    }

    const handleObjectionStatusFilterOverdue = (value) => {
        if (value || value === 0) {
            setViewBackdrop(true)
            dispatch(filterObjectionStatusOverdue(value))
        }
    }

    const handleResetObjectionStatusFilterOverdue = () => {
        dispatch(resetObjectionStatusFilterOverdue())
    }

    const handlePreviousPageOverdue = () => {
        dispatch(previousPageOverdue());
    };

    const handleFirstPageOverdue = () => {
        dispatch(firstPageOverdue());
    };

    const handleLastPageOverdue = (page) => {
      dispatch(lastPageOverdue(typeof page === 'number' ? page : totalPagesOverdue));
    };

    useEffect(() => {
        if ((props.showTable && currentPath == "/amend-2636-bill-table")) {
            if (token) {
                dispatch(fetchPaginatedPartPaymentOverdue({ page: currentPageOverdue, pageSize: pageSizeOverdue, dateFrom: dateFromOverdue, dateTo: dateToOverdue, applicationProgressId: applicationProgressIdOverdue, objectionStatus: objectionStatusOverdue, officeCode: apiOfficeCode }));
            }
        }
    }, [currentPageOverdue, pageSizeOverdue, dateFromOverdue, dateToOverdue, applicationProgressIdOverdue, objectionStatusOverdue, apiOfficeCode]);

    useEffect(() => {
        if (dataOverdue) {
            setDdeApplicationsOverdue(dataOverdue)
            setViewBackdrop(false)
        }
    }, [dataOverdue])

    //#region api function
    const duedateextensionapi = async (x, values) => {
        setViewBackdrop(true)
        const formattedDate = values.split("-").reverse().join("-");
        var formdata = new FormData();
        formdata.append("applicationId", applicationId);
        formdata.append("lastModifiedBy", userId);
        formdata.append("applicationProgressId", x);
        formdata.append("approvedExtendedDueDate", formattedDate);
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);;
        var requestOptions = {
            method: 'POST',
            body: formdata,
            headers: myHeaders,
            redirect: 'follow',
        };
        await fetch(cfpBaseUrl + "dateExtention/dueDateAppProgId", requestOptions)
            .then(response => response.json())
            .then(result => {
                if (result.status === '200') {
                    setViewBackdrop(false)
                    handleClose()
                    // window.location.reload();
                } else if (result.message.startsWith('Since the month end process')) {
                    setError(result.message)
                    setViewBackdrop(false)
                } else if (result.status === '400') {
                    setError(result.message)
                    setViewBackdrop(false)
                }
            })
            .catch(() => {});
    }




    const errorCloses = () => {
        setError('')
    }

    const refreshPartPaymentApplications = () => {
        dispatch(fetchPaginatedPartPaymentNormal({
            page: currentPageNormal,
            pageSize: pageSizeNormal,
            dateFrom: dateFromNormal,
            dateTo: dateToNormal,
            applicationProgressId: applicationProgressIdNormal,
            objectionStatus: objectionStatusNormal,
            officeCode: apiOfficeCode,
        }));
        dispatch(fetchPaginatedPartPaymentOverdue({
            page: currentPageOverdue,
            pageSize: pageSizeOverdue,
            dateFrom: dateFromOverdue,
            dateTo: dateToOverdue,
            applicationProgressId: applicationProgressIdOverdue,
            objectionStatus: objectionStatusOverdue,
            officeCode: apiOfficeCode,
        }));
    };

    const handleClose = () => {
        setOpen(false);
        setFlag_applicantCNICFront(null);
        setFlag_applicantCNICBack(null);
        setFlag_paidBillImageFlag(null);
        setDocuments({});
        setDialogRowData({});
        setSubmittedCnicFrontPreview('');
        setSubmittedCnicBackPreview('');
        setSubmittedDocViewer({ open: false, image: '', name: '' });
        setApiPopup((prev) => ({
            ...prev,
            open: false
        }));
        refreshPartPaymentApplications();
    }

    useEffect(() => {
        let cancelled = false;

        const convertSubmittedDocument = async (src, setPreview) => {
            if (!src) {
                setPreview('');
                return;
            }

            if (isPdfDataUrl(src)) {
                try {
                    const image = await pdfToImage(src);
                    if (!cancelled) {
                        setPreview(image);
                    }
                } catch {
                    if (!cancelled) {
                        setPreview('');
                    }
                }
                return;
            }

            if (!cancelled) {
                setPreview(normalizeDocumentDataUrl(src));
            }
        };

        const convertSubmittedDocuments = async () => {
            await Promise.all([
                convertSubmittedDocument(
                    documnets?.applicantCNICFront,
                    setSubmittedCnicFrontPreview,
                ),
                convertSubmittedDocument(
                    documnets?.applicantCNICBack,
                    setSubmittedCnicBackPreview,
                ),
            ]);
        };

        convertSubmittedDocuments();

        return () => {
            cancelled = true;
        };
    }, [documnets?.applicantCNICFront, documnets?.applicantCNICBack]);

    const handleCloseRemarks = () => {
        setOpenCancelModal(false);
    }

    useScanDetection({
        onComplete: async (code) => {
            if (code) {
                code = code.replace(/Shift/g, '');
                const regex = /^[A-Z]{2}-\d{14,15}$/;
                if (regex.test(code)) {
                    await handleRowClick(code);
                } else {
                }
            }
        },
        minLength: 4, // EAN13
    });



    const getClassName = (x) => {
        if (x === "Pending") {
            return 'pending-button';
        }
        else if (x === "In Process") {
            return 'inprocess-button';
        }
        else if (x === "Approved") {
            return 'approved-button';
        }
        else if (x === "Objection") {
            return 'objection-button';
        }
        else if (x === "Rejected") {
            return 'rejected-button';
        }
    }

    const filterNCApp = async (applicationId) => {
        let myArray = ddeApplicationsNormal
        let totalArray = myArray.concat(ddeApplicationsOverdue)
        let itemFound = totalArray.find(item => item.applicationId === applicationId)

        if (itemFound) {
            setIsRelevantToUser(true)
        }
        else if (itemFound === undefined) {
            itemFound = await SearchIndividualApplication(token, applicationId, '17')
            if (itemFound) {
                setIsRelevantToUser(itemFound.isReleventToUser)
            }
        }
        return itemFound;
    }

    const convertDate = (dateString) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const date = new Date(dateString);
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    }

    const convertDatewithTime = (dateString) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const date = new Date(dateString);
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        const hour = date.getHours();
        const minute = date.getMinutes();
        const second = date.getSeconds();
        return `${day}-${month}-${year} ${hour}:${minute}:${second}`;
    }

    const apiToDataGridConversionNormal = () => {

        const tempArray = []
        if (ddeApplicationsNormal) {
            ddeApplicationsNormal.forEach((item) => {
                let dataGridObject = {}
                if (item.applicationId) {
                    if (item.objectionStatus === 0) {
                        dataGridObject = {
                            "applicationId": item.applicationId,
                            "creationDate": convertDate(item.creationDate),
                            //"connectionType": item.connectionType,
                            "referenceNo": item.referenceNo,
                            "name": item.applicantName,
                            "dueDate": convertDate(item.currentBillDueDate),
                            // "extendedDueDate": item.appliedExtentionOfDueDate,
                            "applicationStatusId": statusFunc(item.applicationProgressId, item.objectionStatus),
                            "applicationProgressId": item.applicationProgressId,
                            "releventApplicationFlage": item.releventApplicationFlage,
                        }
                    }
                    else if (item.objectionStatus === 1) {
                        dataGridObject = {
                            "applicationId": item.applicationId,
                            "creationDate": convertDate(item.creationDate),
                            //"connectionType": item.connectionType,
                            "referenceNo": item.referenceNo,
                            "name": item.applicantName,
                            "dueDate": convertDate(item.currentBillDueDate),
                            // "extendedDueDate": item.appliedExtentionOfDueDate,
                            "applicationStatusId": statusFunc(item.applicationProgressId, item.objectionStatus),
                            "applicationProgressId": item.applicationProgressId,
                            "releventApplicationFlage": item.releventApplicationFlage,
                        }
                    }
                    tempArray.push(dataGridObject)
                }
            })
        }
        else {
            const dataGridObject = {
                "applicationId": ' ',
                "connectionType": '',
                // "appliedLoad": item.appliedLoad,
                // "duration": item.duration,
                "applicantName": ' ',
                "applicantCNIC": ' ',
                "applicationStatusId": statusFunc(' '),
            }
            tempArray.push(dataGridObject)
        }
        setDdeApplicationsNormalToDataGrid(tempArray);
    }

    const apiToDataGridConversionOverdue = () => {

        const tempArray = []
        if (ddeApplicationsOverdue) {
            ddeApplicationsOverdue.forEach((item) => {
                let dataGridObject = {}
                if (item.applicationId) {
                    if (item.objectionStatus === 0) {
                        dataGridObject = {
                            "applicationId": item.applicationId,
                            "creationDate": convertDate(item.creationDate),
                            //"connectionType": item.connectionType,
                            "referenceNo": item.referenceNo,
                            "name": item.applicantName,
                            "dueDate": convertDate(item.currentBillDueDate),
                            // "extendedDueDate": convertDate(item.appliedExtentionOfDueDate),
                            "applicationStatusId": statusFunc(item.applicationProgressId, item.objectionStatus),
                            "applicationProgressId": progFunc(item.applicationProgressId),
                            "releventApplicationFlage": item.releventApplicationFlage,
                        }
                    }
                    else if (item.objectionStatus === 1) {
                        dataGridObject = {
                            "applicationId": item.applicationId,
                            "creationDate": convertDate(item.creationDate),
                            //"connectionType": item.connectionType,
                            "referenceNo": item.referenceNo,
                            "name": item.applicantName,
                            "dueDate": convertDate(item.currentBillDueDate),
                            // "extendedDueDate": convertDate(item.appliedExtentionOfDueDate),
                            "applicationStatusId": statusFunc(item.applicationProgressId, item.objectionStatus),
                            "applicationProgressId": progFunc(item.applicationProgressId),
                            "releventApplicationFlage": item.releventApplicationFlage,
                        }
                    }
                    tempArray.push(dataGridObject)
                }
            })
        }
        else {
            const dataGridObject = {
                "applicationId": ' ',
                "connectionType": '',
                // "appliedLoad": item.appliedLoad,
                // "duration": item.duration,
                "applicantName": ' ',
                "applicantCNIC": ' ',
                "applicationStatusId": statusFunc(' '),
            }
            tempArray.push(dataGridObject)
        }
        setDdeApplicationsOverdueToDataGrid(tempArray);
    }

    const statusFunc = (x, objection) => {
        if (x === 0) {
            return 'In Process'
        }
        else if (x === 1) {
            return 'Approved'
        }
        else if (x === -1) {
            return 'Rejected'
        }
    }

    const progFunc = (x) => {
        if (x === 0) {
            return 'Documents Uploading'
        }
        else if (x === 1) {
            return 'Documents Verification'
        }
        // else if (x === 2) {
        //     return 'Arrears Verification'
        // }
        // else if (x === 3) {
        //     return 'Field Verification'
        // }
        // else if (x === 4) {
        //     return 'Demand Note Issued'
        // }
        // else if (x === 5) {
        //     return 'DN Payment Verification'
        // }
        // else if (x === 6) {
        //     return 'Meter Installation'
        // }
        // else if (x === 7) {
        //     return 'Ready for Billing'
        // }
    }
    const formatToDDMMYYYY = (dateString) => {
        if (!dateString) return "-";

        const [year, month, day] = dateString.split("-");
        return `${day}-${month}-${year}`;
    };
    const handleRowClick = async (applicationId, event) => {
        setViewBackdrop(true);
        const item = await filterNCApp(applicationId);
        if (item) {
            setDialogRowData(item);
            setFlag_applicantCNICFront(item.applicantCNICFrontFlag);
            setFlag_applicantCNICBack(item.applicantCNICBackFlag);
            setFlag_paidBillImageFlag(item.paidBillImageFlag);
            setApplicantCNICFrontObjectionRemarks(item.applicantCNICFrontRemarks || item.applicantCNICBackRemarks || '');
            setApplicantCNICBackObjectionRemarks(item.applicantCNICBackRemarks || item.applicantCNICFrontRemarks || '');
            setPaidBillImageObjectionRemarks(item.paidBillImageRemarks || '');
            // setFlag_electricityBillDocument(item.electricityBillFlag);

            let dataforTable = [];

            if (item.applicationProgressId === 0) {
                dataforTable.push({
                    date: convertDate(item.creationDate),
                    processedBy: item.createdBy,
                    stepName: "Application Submitted",
                    time: convertDatewithTime(item.creationDate).slice(11, 17),
                    remarks: 'Application Submitted.'
                });
            }
            else if (item.applicationProgressId === 1 || (item.applicationProgressId === 2  && item.objectionStatus !== 1)) {
                // Only show Application Submitted for In Process
                dataforTable.push({
                    date: convertDate(item.creationDate),
                    processedBy: item.createdBy,
                    stepName: "Application Submitted",
                    time: convertDatewithTime(item.creationDate).slice(11, 17),
                    remarks: 'Application Submitted.'
                });

            }
            else if (item.applicationProgressId === 3 || (item.applicationProgressId === 2 && item.objectionStatus === 1)) {
                // Show Action Taken and Application Submitted for Completed
                dataforTable.push({
                    stepName: "Action Taken",
                    processedBy: item.lastModifiedBy || 'N/A',
                    date: item.lastModificationDate ? convertDatewithTime(item.lastModificationDate) : 'N/A',
                    remarks: (item.applicationProgressId === 2 && item.objectionStatus === 1) ? 'Reject' : item.remarks || '-',
                });

                dataforTable.push({
                    date: convertDate(item.creationDate),
                    processedBy: item.createdBy,
                    stepName: "Application Submitted",
                    time: convertDatewithTime(item.creationDate).slice(11, 17),
                    remarks: 'Application Submitted.'
                });

            }

            setTableDataProcessingSummary(dataforTable);

            setapllicationid(item.applicationId)
            await getBillAdjustmentDocuments(item.applicationId);
            setViewBackdrop(false);
            setOpen(true);
        } else {
            setViewBackdrop(false);
        }
    };

    const openDetailsPopUp = async (stepName) => {
        let id = dialogRowData.applicationId

        if (stepName === "APPLICATION SUBMITTED" || stepName === "Application Submitted") {
            setApplicationSubmittedPopUp(true)
        }
        else if (stepName === "ACTION TAKEN" || stepName === "Action Taken") {
            setActionTakenPopup(true)
        }
    }

    const stepName = (x) => {
        if (x === 0) {
            return 'Application Submission'
        }
        else if (x === 1 || x === -1) {
            return 'Action Taken'
        }
    };

    //#endregion

    //#endregion

    //#region useEffects

    useEffect(() => {
        if (ddeApplicationsNormal) {
            apiToDataGridConversionNormal()
        }
    }, [ddeApplicationsNormal]);

    useEffect(() => {
        if (ddeApplicationsOverdue) {
            apiToDataGridConversionOverdue()
        }
    }, [ddeApplicationsOverdue]);

    useEffect(() => {
        if (foundId && foundId !== '') {
            setFoundInDDE(foundId)
        }
    }, [foundId])

    useLayoutEffect(() => {
        if (foundInDDE) {
            handleRowClick(foundInDDE)
        }
    }, [foundInDDE])

    const getDayName = (dateString) => {
        if (!dateString) return "-";

        let date;

        if (dateString.includes("-") && dateString.split("-")[0].length === 4) {
            const [year, month, day] = dateString.split("-");
            date = new Date(Number(year), Number(month) - 1, Number(day));
        } else if (dateString.includes("-")) {
            const [day, monthStr, year] = dateString.split("-");
            const month = new Date(Date.parse(monthStr + " 1, 2020")).getMonth();
            date = new Date(Number(year), month, Number(day));
        } else {
            return "-";
        }

        if (isNaN(date)) return "-";
        return date.toLocaleString("en-US", { weekday: "long" });
    };
    const formatDateToShortMonth = (dateString) => {
        if (!dateString) return "-";

        const [day, month, year] = dateString.split("-");
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        const monthIndex = parseInt(month, 10) - 1;
        if (monthIndex < 0 || monthIndex > 11) return "-";

        return `${day}-${months[monthIndex]}-${year}`;
    };


    //#endregion

    const tblColumns = [
        { field: "applicationId", headerName: 'Application No.', align: 'center', sortable: false, filterable: false, headerStyle: tableStyle.headerStyle, headerCellStyle: tableStyle.headerCellStyle, rowCellStyle: tableStyle.rowCellStyle },
        { field: "creationDate", type: 'dateTime', headerName: 'Submission Date', align: 'center', sortable: false, filterable: false, headerStyle: tableStyle.headerStyle, headerCellStyle: tableStyle.headerCellStyle, rowCellStyle: tableStyle.rowCellStyle },
        { field: "referenceNo", headerName: 'Reference No.', align: 'center', sortable: false, filterable: false, headerStyle: tableStyle.headerStyle, headerCellStyle: tableStyle.headerCellStyle, rowCellStyle: tableStyle.rowCellStyle },
        { field: "name", headerName: 'Applicant Name', align: 'left', sortable: false, filterable: false, headerStyle: tableStyle.headerStyle, headerCellStyle: tableStyle.headerCellStyle, rowCellStyle: tableStyle.rowCellStyle },
        { field: "dueDate", type: 'dateTime', headerName: 'Due Date', align: 'center', sortable: false, filterable: false, headerStyle: tableStyle.headerStyle, headerCellStyle: tableStyle.headerCellStyle, rowCellStyle: tableStyle.rowCellStyle },
        {
            field: "applicationProgressId",
            headerStyle: tableStyle.headerStyle,
            headerCellStyle: tableStyle.headerCellStyle,
            rowCellStyle: tableStyle.rowCellStyle,
            headerName: "Status",
            align: 'center',
            sortable: false,
            filterable: false,
            renderCell: (row) => {
                let btnText = row.applicationProgressId === 1 ? 'In Process' : (row.applicationProgressId === 2 && row.objectionStatus != 1) ? 'In Process' : (row.applicationProgressId === 2 && row.objectionStatus == 1) ? 'Rejected' : (row.applicationProgressId === 3) ? 'Approved' : (row.applicationProgressId === 4) ? 'Approved' : '-';
                // let textColor = row.applicationProgressId === 1 ? '#0d6efd' : row.applicationProgressId === 2 ? '#198754' : '#6c757d';
                let textColor = row.applicationProgressId === 1 ? '#0d6efd' : (row.applicationProgressId === 2 && row.objectionStatus != 1) ? '#0d6efd' : (row.applicationProgressId === 2 && row.objectionStatus == 1) ? '#E11B3C' : (row.applicationProgressId === 3) ? '#2e7d32' : (row.applicationProgressId === 4) ? '#b7e1c0' : '-';

                return (
                    <div style={{
                        color: textColor,
                        fontWeight: 600
                    }}>
                        {btnText}
                    </div>
                )
            }
        },
    ]

    const tblColumnsOverDue = [
        { field: "applicationId", headerName: 'Application No.', align: 'center', sortable: false, filterable: false, headerStyle: tableStyleOverDue.headerStyle, headerCellStyle: tableStyleOverDue.headerCellStyle, rowCellStyle: tableStyleOverDue.rowCellStyle },
        { field: "creationDate", type: 'dateTime', headerName: 'Submission Date', align: 'center', sortable: false, filterable: false, headerStyle: tableStyleOverDue.headerStyle, headerCellStyle: tableStyleOverDue.headerCellStyle, rowCellStyle: tableStyleOverDue.rowCellStyle },
        { field: "referenceNo", headerName: 'Reference No.', align: 'center', sortable: false, filterable: false, headerStyle: tableStyleOverDue.headerStyle, headerCellStyle: tableStyleOverDue.headerCellStyle, rowCellStyle: tableStyleOverDue.rowCellStyle },
        { field: "name", headerName: 'Applicant Name', align: 'left', sortable: false, filterable: false, headerStyle: tableStyleOverDue.headerStyle, headerCellStyle: tableStyleOverDue.headerCellStyle, rowCellStyle: tableStyleOverDue.rowCellStyle },
        { field: "dueDate", type: 'dateTime', headerName: 'Due Date', align: 'center', sortable: false, filterable: false, headerStyle: tableStyleOverDue.headerStyle, headerCellStyle: tableStyleOverDue.headerCellStyle, rowCellStyle: tableStyleOverDue.rowCellStyle },
        {
            field: "applicationProgressId",
            headerStyle: tableStyleOverDue.headerStyle,
            headerCellStyle: tableStyleOverDue.headerCellStyle,
            rowCellStyle: tableStyleOverDue.rowCellStyle,
            headerName: "Status",
            align: 'center',
            sortable: false,
            filterable: false,
            renderCell: (row) => {
                let btnText = row.applicationProgressId === 1 ? 'In Process' : row.applicationProgressId === 2 ? 'Approved' : '-';
                let textColor = row.applicationProgressId === 1 ? '#0d6efd' : row.applicationProgressId === 2 ? '#198754' : '#6c757d';
                return (
                    <div style={{
                        color: textColor,
                        fontWeight: 600
                    }}>
                        {btnText}
                    </div>
                )
            }
        },
    ]

    useImperativeHandle(ref, () => ({
        handleRowClick
    }));

    const location = useLocation()
    const currentPath = location.pathname;

    useEffect(() => {
        if (location.state?.refreshPartPaymentList) {
            refreshPartPaymentApplications();
            navigate(currentPath, { replace: true, state: {} });
        }
    }, [location.state?.refreshPartPaymentList]);

    const ranges = [
        { max: 25000, perm: '1 - 25,000' },
        { max: 50000, perm: '25,001 - 50,000' },
        { max: 100000, perm: '50,001 - 100,000' },
        { max: 200000, perm: '100,001 - 200,000' },
        { max: 300000, perm: '200,001 - 300,000' },
        { max: 500000, perm: '300,001 - 500,000' },
        { max: 1000000, perm: '500,001 - 1,000,000' },
        { max: 20000000, perm: '1,000,001 - 20,000,000' },
        { max: Infinity, perm: '20,000,001 - MAX' },
    ];

    const hasReuploadedDocsPendingVerification =
        [dialogRowData.applicantCNICFrontFlag, dialogRowData.applicantCNICBackFlag,
            documnets?.applicantCNICFrontFlag, documnets?.applicantCNICBackFlag,
            documnets?.paidBillImageFlag, dialogRowData.paidBillImageFlag]
            .some((flag) => Number(flag) === 2);

    const isDocumentReviewDecided = (flag) => Number(flag) === 1 || Number(flag) === -1;

    const canSubmitPartPaymentDocumentVerification =
        isDocumentReviewDecided(flag_applicantCNICFront) &&
        isDocumentReviewDecided(flag_applicantCNICBack) &&
        (!documnets?.paidBillImage || isDocumentReviewDecided(flag_paidBillImageFlag));

    const canApprove = (billAmount) => {
        for (let i = 0; i < ranges.length; i++) {
            const { max, perm } = ranges[i];

            const hasPermission = CheckPermissions(
                permissions,
                'General Services', 'Part Payment', 'Workflow Permissions',
                perm
            );

            if (hasPermission && billAmount <= max) {
                return true;
            }
        }
        return false;
    };

    const formatMonthYear = (dateString) => {
        if (!dateString) return '-';
        const cleanDateString = dateString.split('T')[0];
        const date = new Date(cleanDateString);
        if (isNaN(date)) return '-';
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${month}-${year}`;
    };

    const isExtensionDateExpired = (dateString) => {
        if (!dateString) return false;

        const appliedDate = new Date(dateString);
        const today = new Date();


        appliedDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        return appliedDate < today;
    };



    const remarksFormik = useFormik({
        initialValues: {
            iv_remarks: "",
            // Add iv_approved_due_date if it’s part of the form
            // iv_approved_due_date: "",
        },
        validationSchema: Yup.object({
            iv_remarks: Yup.string()
                .min(10, "Remarks should be at least 10 characters.")
                .max(100, "Remarks should be less than 100 characters.")
                .required("Enter remarks."),
            // Add validation for iv_approved_due_date if needed
            // iv_approved_due_date: Yup.string().required("Date is required."),
        }),
        onSubmit: async (values) => {
            const x = -1;
            const formattedDate = dialogRowData.appliedExtentionOfDueDate
            setViewBackdrop(true);
            try {
                const formdata = new FormData();
                //   formdata.append("remarks", values.iv_remarks); 
                formdata.append("applicationId", applicationId);
                formdata.append("lastModifiedBy", userId);
                formdata.append("applicationProgressId", x);
                formdata.append("approvedExtendedDueDate", formattedDate);
                const myHeaders = new Headers();
                myHeaders.append("Authorization", token);
                const requestOptions = {
                    method: 'POST',
                    body: formdata,
                    headers: myHeaders,
                    redirect: 'follow',
                };
                await fetch(cfpBaseUrl + "dateExtention/dueDateAppProgId", requestOptions)
                    .then(response => response.json())
                    .then(result => {
                        if (result.status === '200') {
                            setOpenCancelModal(false);
                            handleClose();
                        } else if (result.message?.startsWith('Since the month end process')) {
                            setError(result.message);
                        } else {
                            setError('Failed to submit the application.');
                        }
                    })
                    .catch(() => {});
            } catch (e) {
            }
            setViewBackdrop(false);

        },
    });

    const hideCopyOfApplicantCNICVerifyAlert = (x) => {
        setCopyOfApplicantCNICVerifyPopUp(x);
    };

    const hidePropertyDocVerifyAlert = (x) => {
        setPaidBillImagePopUp(x);
    };

    const callingUpdateDocumentStatusAPI = async () => {
        setViewBackdrop(true);
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        let progId = null;
        let objStat = null;
        let remrks = "";
        let remarksDetail = "";

        if (flag_applicantCNICFront === -1 || flag_applicantCNICBack === -1) {
            progId = 1;
            objStat = 1;
            remrks = "Documents Rejected.";
            remarksDetail = "Documents Objection in : ".concat(
                flag_applicantCNICFront === -1 ? "CNIC Front, " : "",
                flag_applicantCNICBack === -1 ? "CNIC Back" : ""
            );
        } else if (flag_applicantCNICFront === 1 && flag_applicantCNICBack === 1) {
            progId = 2;
            objStat = 0;
            remrks = "Documents Verified.";
            remarksDetail = "CNIC Front Verified. CNIC Back Verified.";
        }


        const raw = JSON.stringify({
            applicationId: dialogRowData.applicationId,
            applicationProgressId: progId,
            objectionStatus: objStat,
            remarks: remrks,
            remarksDetail: remarksDetail,
            applicantCNICFrontFlag: flag_applicantCNICFront,
            applicantCNICBackFlag: flag_applicantCNICBack,
            applicantCNICFrontRemarks: applicantCNICFrontObjectionRemarks,
            applicantCNICBackRemarks: applicantCNICBackObjectionRemarks,
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow",
        };

        await fetch(cfpBaseUrl + "billAdjustment/partPayment/verifyDocuments", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result.status === "200") {
                    setOpen(false);
                    if (progId === 2 && objStat === 0) {
                        setResultPopup('verified');
                    } else if (objStat === 1) {
                        setResultPopup('docsRejected');
                    } else {
                        handleClose();
                    }
                } else {
                    setError(result.message || "Failed to update document status");
                }
                setViewBackdrop(false);
            })
            .catch((error) => {
                setError("Failed to update document status");
                setViewBackdrop(false);
            });
    };

    const updateDocumentStatus = () => {
        if (canSubmitPartPaymentDocumentVerification) {
            callingUpdateDocumentStatusAPI();
        } else {
            setError("Accept or reject each document before verifying.");
        }
    };



    let CONStatusBarArray = [
        {
            title1: "DOCUMENT",
            title2: "SUBMISSION",
        },
        {
            title1: "DOCUMENT",
            title2: "VERIFICATION",
        },
        {
            title1: "PROCESS",
            title2: "PART PAYMENT",
        },
        {
            title1: "COMPLETED",
            title2: "",
        },
    ];

    const getPartPaymentStepBarProgressId = (progressId) => {
        const id = Number(progressId) || 0;
        const lastStepIndex = CONStatusBarArray.length - 1;
        return id >= lastStepIndex ? CONStatusBarArray.length : id;
    };


    const getBillAdjustmentDocuments = async (appId) => {
        setDocumentsLoading(true);
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            "applicationId": appId
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        await fetch(cfpBaseUrl + "billAdjustment/partPayment/getDocuments", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result.status === "200" && result.data?.length > 0) {
                    setDocuments(result.data[0]);
                } else {
                    setDocuments({});
                }
                setDocumentsLoading(false);
            })
            .catch((error) => {
                setDocuments({});
                setDocumentsLoading(false);
            });
    }



    const updateDocFlag = (documentName, value, remarks) => {
        switch (documentName) {
            case "copyOfApplicantCNIC":
                setFlag_applicantCNICFront(value);
                setFlag_applicantCNICBack(value);
                setApplicantCNICFrontObjectionRemarks(remarks);
                setApplicantCNICBackObjectionRemarks(remarks);
                setCopyOfApplicantCNICVerifyPopUp(false);
                break;
            case "paidBillImage":
                setFlag_paidBillImageFlag(value);
                setPaidBillImageObjectionRemarks(remarks);
                setPaidBillImagePopUp(false);
                break;

            default:
                break;
        }
    };



    const partPaymentClaim = (value) => {
        // Validation for Approved Part Payment field
        if (!approvedPayment || approvedPayment === '' || Number(approvedPayment) <= 0) {
            setApprovedPaymentError('Please enter a valid amount (must be greater than zero)');
            return;
        }
        setApprovedPaymentError(''); // Clear error if valid

        if (value === 'Approve') {
            setViewBackdrop(true);
            const myHeaders = new Headers();
            myHeaders.append("Authorization", token);
            myHeaders.append("Content-Type", "application/json");

            const raw = JSON.stringify({
                "applicationId": dialogRowData.applicationId,
                "applicationProgressId": 3,
                "objectionStatus": 0,
                "remarks": "Approved",
                "remarksDetail": "Approved for IBS",
                "approvedPartPayment": approvedPayment
            });

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: raw,
                redirect: "follow"
            };

            fetch(cfpBaseUrl + "/billAdjustment/partPayment/process", requestOptions)
                .then((response) => response.json())
                .then((result) => {
                    setViewBackdrop(false);
                    // Show Bill Amended Successfully popup on Approve
                    setApiPopup({
                        open: true,
                        type: 'success',
                        message: result.message || `Part Payment adjusted successfully. Approved Amount: Rs. ${Number(approvedPayment).toLocaleString()}.`,
                    });
                })
                .catch((error) => {
                    setViewBackdrop(false);
                });
        } else {
            setViewBackdrop(true);
            const myHeaders = new Headers();
            myHeaders.append("Authorization", token);
            myHeaders.append("Content-Type", "application/json");

            const raw = JSON.stringify({
                "applicationId": dialogRowData.applicationId,
                "applicationProgressId": 2,
                "objectionStatus": 1,
                "remarks": "Approved",
                "remarksDetail": "Approved for IBS",
                "approvedPartPayment": approvedPayment
            });

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: raw,
                redirect: "follow"
            };

            fetch(cfpBaseUrl + "/billAdjustment/partPayment/process", requestOptions)
                .then((response) => response.json())
                .then(() => {
                    setViewBackdrop(false);
                    setResultPopup('reject');
                })
                .catch((error) => {
                    setViewBackdrop(false);
                });
        }

    }

    const handleCloseResultPopup = () => {
        if (resultPopup) {
            setResultPopup(null);
            handleClose();
            return;
        }
        setApiPopup({ open: false, type: '', message: '' });
        handleClose();
    };

    const ppResultDialogConfig = {
        verified: {
            Icon: CheckCircleIcon,
            iconClassName: 'dde-result-dialog__icon dde-result-dialog__icon--approve',
            title: 'Verified Successfully',
            message: 'Part Payment documents verified successfully.',
            showBill: false,
        },
        docsRejected: {
            Icon: HighlightOffIcon,
            iconClassName: 'dde-result-dialog__icon dde-result-dialog__icon--reject',
            title: 'Documents Rejected',
            message: 'Part Payment application documents have been rejected.',
            showBill: false,
        },
        success: {
            Icon: CheckCircleIcon,
            iconClassName: 'dde-result-dialog__icon dde-result-dialog__icon--approve',
            title: 'Request Approved',
            message: 'Part Payment request is approved successfully.',
            showBill: true,
        },
        error: {
            Icon: HighlightOffIcon,
            iconClassName: 'dde-result-dialog__icon dde-result-dialog__icon--reject',
            title: 'Request Failed',
            showBill: false,
        },
        reject: {
            Icon: HighlightOffIcon,
            iconClassName: 'dde-result-dialog__icon dde-result-dialog__icon--reject',
            title: 'Request Rejected',
            message: 'Part Payment request is Rejected successfully.',
            showBill: false,
        },
    };

    const activePpResultDialog = resultPopup
        ? ppResultDialogConfig[resultPopup]
        : apiPopup.open && apiPopup.type
            ? ppResultDialogConfig[apiPopup.type]
            : null;

    const activeResultMessage = resultPopup
        ? ppResultDialogConfig[resultPopup]?.message
        : apiPopup.message;

    useEffect(() => {
        if (dialogRowData?.approvedPartPayment) {
            setApprovedPayment(dialogRowData.approvedPartPayment);
        } else if (dialogRowData?.partPaymentAmount) {
            setApprovedPayment(dialogRowData.partPaymentAmount);
        }
    }, [
        dialogRowData?.applicationId,
        dialogRowData?.approvedPartPayment,
        dialogRowData?.partPaymentAmount,
    ]);
    return (
        <>
            <ServiceDashboardTables
                enabled={props.showTable && currentPath === "/amend-2636-bill-table"}
                isLoadingNormal={isLoad}
                isLoadingOverdue={isLoadd}
                normalTitle={`Part Payment ( ${numberFormatting(totalRecordsNormal) ? numberFormatting(totalRecordsNormal) : "-"} )`}
                overdueCount={numberFormatting(totalRecordsOverdue) ? numberFormatting(totalRecordsOverdue) : "-"}
                normalColumns={tblColumns}
                overdueColumns={tblColumnsOverDue}
                normalData={ddeApplicationsNormalToDataGrid}
                overdueData={ddeApplicationsOverdueToDataGrid}
                onRowClick={handleRowClick}
                mobileFields={["applicationId", "name", "applicationProgressId"]}
                showCompanyFilter={officeCode == 10}
                companies={DISCO_COMPANIES}
                selectedOfficeCode={selectedOfficeCode}
                onOfficeCodeChange={setSelectedOfficeCode}
                normalTableProps={{
                    firstPage: handleFirstPage,
                    lastPage: handleLastPage,
                    totalPages: totalPagesNormal,
                    totalRecords: totalRecordsNormal,
                    currentPage: currentPageNormal,
                    handleFilterDateFrom: handleDateFromFilter,
                    handleFilterDateTo: handleDateToFilter,
                    resetDateFilter: handleResetDateFilter,
                    handleFilterProgressId: handleProgressIdFilter,
                    handleFilterObjectionStatus: handleObjectionStatusFilter,
                    resetProgressIdFilter: handleResetProgressIdFilter,
                    resetObjectionStatusFilter: handleResetObjectionStatusFilter,
                    nextPage: handleNextPage,
                    previousPage: handlePreviousPage,
                }}
                overdueTableProps={{
                    firstPage: handleFirstPageOverdue,
                    nextPage: handleNextPageOverdue,
                    previousPage: handlePreviousPageOverdue,
                    lastPage: handleLastPageOverdue,
                    handleFilterDateFrom: handleDateFromFilterOverdue,
                    handleFilterDateTo: handleDateToFilterOverdue,
                    resetDateFilter: handleResetDateFilterOverdue,
                    resetProgressIdFilter: handleResetProgressIdFilterOverdue,
                    resetObjectionStatusFilter: handleResetObjectionStatusFilterOverdue,
                    handleFilterProgressId: handleProgressIdFilterOverdue,
                    handleFilterObjectionStatus: handleObjectionStatusFilterOverdue,
                    totalPages: totalPagesOverdue,
                    totalRecords: totalRecordsOverdue,
                    currentPage: currentPageOverdue,
                }}
            />

            {/* Main Popup */}
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth={true}
                maxWidth="xl"
                {...workflowDialogShellProps(isWorkflowMobile)}
                className={workflowDialogClassName('main-dialog pp-workflow-dialog', isWorkflowMobile)}
                PaperProps={{ className: 'pp-workflow-dialog-paper' }}
            >
                <DialogTitle className="dialog-title">
                    <div className="pp-dialog-header">
                        <Typography className="applicationTitleText">
                            Part Payment -
                            <span style={{ color: 'white' }}>
                                {(dialogRowData.applicationProgressId == 1) ? ' Document Verification' : (dialogRowData.applicationProgressId == 2) ? ' Process Part Payment' : (dialogRowData.applicationProgressId == 3) ? ' Completed' : ' Application Submission'}
                            </span>
                        </Typography>
                        <button type="button" className="closeIcon" onClick={handleClose} aria-label="Close dialog">
                            <CloseIcon id="clearPopup" sx={{ color: 'white' }} />
                        </button>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText className="dialog-content">
                        <Grid container className="headercontainer">
                            <Grid item lg={12} className="status-bar">
                                <StepProgressBar
                                    array={CONStatusBarArray}
                                    applicationProgressId={getPartPaymentStepBarProgressId(dialogRowData.applicationProgressId)}
                                    pageName="Part Payment"
                                    disableStepClick
                                />
                            </Grid>
                            <Grid item xs={12} className="application-details">
                                <div className="pp-application-details-body">
                                    <p className="pp-section-label">Application Details</p>
                                    <div className="pp-details-grid">
                                        <div className="first-row-item">
                                            <p className="prop-title">Application Id.</p>
                                            <p className="prop-detail">{dialogRowData.applicationId || '-'}</p>
                                        </div>
                                        <div className="first-row-item">
                                            <p className="prop-title">Reference No.</p>
                                            <p className="prop-detail">{dialogRowData.referenceNo || '-'}</p>
                                        </div>
                                        <div className="first-row-item">
                                            <p className="prop-title">Subdivision</p>
                                            <p className="prop-detail">{subdivisionName(dialogRowData.subDivisionCode, subDivisions)}</p>
                                        </div>
                                        <div className="first-row-item">
                                            <p className="prop-title">Name</p>
                                            <p className="prop-detail">{dialogRowData.applicantName || '-'}</p>
                                        </div>
                                        <div className="first-row-item">
                                            <p className="prop-title">CNIC</p>
                                            <p className="prop-detail">{dialogRowData.applicantCNIC || '-'}</p>
                                        </div>
                                        <div className="first-row-item">
                                            <p className="prop-title">Current Bill Amount</p>
                                            <p className="prop-detail">
                                                {dialogRowData.currentBillAmount
                                                    ? Number(dialogRowData.currentBillAmount).toLocaleString()
                                                    : '-'}
                                            </p>
                                        </div>
                                        <div className="first-row-item">
                                            <p className="prop-title">Current Bill Month</p>
                                            <p className="prop-detail">{convertDate(dialogRowData.currentBillMonth) || '-'}</p>
                                        </div>
                                        <div className="first-row-item">
                                            <p className="prop-title">Claiment Payment</p>
                                            <p className="prop-detail">
                                                {dialogRowData.partPaymentAmount
                                                    ? Number(dialogRowData.partPaymentAmount).toLocaleString()
                                                    : '-'}
                                            </p>
                                        </div>
                                        <div className="first-row-item">
                                            <p className="prop-title">Approved Payment</p>
                                            <p className="prop-detail">
                                                {dialogRowData.approvedPartPayment
                                                    ? Number(dialogRowData.approvedPartPayment).toLocaleString()
                                                    : '-'}
                                            </p>
                                        </div>
                                        <div className="first-row-item pp-details-grid__address">
                                            <p className="prop-title">Address</p>
                                            <p className="prop-detail">{dialogRowData.applicantAddress || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            </Grid>
                            <Grid item className='processing-summary'>
                                <p className='title'>Activity Log</p>
                                <ProcessingSummaryTableBilling data={tableDataProcessingSummary} columns={tableColumnsProcessingSummary} openDetailsPopUp={openDetailsPopUp} />
                            </Grid>
                            <Grid item className="action-form">
                                <div className="fields">
                                    {dialogRowData.applicationProgressId === 0 ? (
                                        <>
                                            <h3 className="step-title">Documents Pending</h3>
                                            <div className="description-box">

                                            </div>
                                        </>
                                    ) : dialogRowData.applicationProgressId === 1 &&
                                        (dialogRowData.objectionStatus === 0 ||
                                            dialogRowData.objectionStatus === 2) ? (
                                        <>
                                            {CheckPermissions(permissions, 'General Services', 'Part Payment', 'Workflow Permissions', 'Document Verification') ? (
                                                <>
                                                    <h3 className="step-title">Document Verification</h3>
                                                    <Grid
                                                        className="document-verification-container"
                                                        container
                                                        spacing={1}
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "flex-start",
                                                            marginTop: "1%",
                                                            padding: "2%",
                                                        }}
                                                    >
                                                        {(documnets?.applicantCNICFront ||
                                                            documnets.applicantCNICBack) && (
                                                                <Grid item lg={2}>
                                                                    <Grid
                                                                        container
                                                                        sx={{
                                                                            backgroundColor:
                                                                                flag_applicantCNICFront !== null
                                                                                    ? getDocumentBackGroundColor(
                                                                                        flag_applicantCNICFront
                                                                                    )
                                                                                    : getDocumentBackGroundColor(
                                                                                        documnets?.applicantCNICFrontFlag
                                                                                    ),
                                                                            borderRadius: "10px",
                                                                            minHeight: "150px",
                                                                            height: "auto",
                                                                        }}
                                                                    >
                                                                        <Grid
                                                                            item
                                                                            lg={12}
                                                                            style={{
                                                                                display: "flex",
                                                                                justifyContent: "center",
                                                                                alignItems: "center",
                                                                                paddingBottom: "5px",
                                                                            }}
                                                                        >
                                                                            <div
                                                                                onClick={() => {
                                                                                    if (!copyOfApplicantCNICVerifyPopUp) {
                                                                                        setCopyOfApplicantCNICVerifyPopUp(true);
                                                                                    }
                                                                                }}
                                                                                style={{
                                                                                    display: "flex",
                                                                                    justifyContent: "space-between",
                                                                                    flexDirection: "column",
                                                                                    alignItems: "center",
                                                                                }}
                                                                            >
                                                                                <>
                                                                                    <DocumentPreviewThumb
                                                                                        src={documnets.applicantCNICFront}
                                                                                    />
                                                                                    <DocumentPreviewThumb
                                                                                        src={documnets.applicantCNICBack}
                                                                                    />
                                                                                    {flag_applicantCNICFront === 1 ? (
                                                                                            <img
                                                                                                src={tickicon}
                                                                                                width="25px"
                                                                                                height="25px"
                                                                                                alt="Tick Icon"
                                                                                                className="doc-verification-status-icon"
                                                                                            />
                                                                                    ) : flag_applicantCNICFront === -1 ? (
                                                                                            <img
                                                                                                src={crossIcon}
                                                                                                width="25px"
                                                                                                height="25px"
                                                                                                alt="Cross Icon"
                                                                                                className="doc-verification-status-icon"
                                                                                            />
                                                                                    ) : null}
                                                                                {copyOfApplicantCNICVerifyPopUp && (
                                                                                    <OpenBase64ImageInPopup
                                                                                        docVerifyPopUp={
                                                                                            copyOfApplicantCNICVerifyPopUp
                                                                                        }
                                                                                        hideDocVerifyPopup={
                                                                                            hideCopyOfApplicantCNICVerifyAlert
                                                                                        }
                                                                                        documentName="Applicant CNIC"
                                                                                        documentImage={
                                                                                            documnets.applicantCNICFront
                                                                                        }
                                                                                        backDocumentImage={
                                                                                            documnets.applicantCNICBack
                                                                                        }
                                                                                        width={"100%"}
                                                                                        document={
                                                                                            documnets.applicantCNICFront
                                                                                        }
                                                                                        documentFlag={
                                                                                            documnets.applicantCNICFrontFlag
                                                                                        }
                                                                                        applicationProgressId={
                                                                                            dialogRowData.applicationProgressId
                                                                                        }
                                                                                        objectionStatus={
                                                                                            dialogRowData.objectionStatus
                                                                                        }
                                                                                        updateDocFlag={updateDocFlag}
                                                                                        documentRemarks={
                                                                                            applicantCNICBackObjectionRemarks
                                                                                        }
                                                                                        titleToUpdateFlag="copyOfApplicantCNIC"
                                                                                        flag_document={flag_applicantCNICFront}
                                                                                        flag_documentBack={
                                                                                            flag_applicantCNICBack
                                                                                        }
                                                                                        verificationPermissionService="Part Payment"
                                                                                    />
                                                                                )}
                                                                                </>
                                                                            </div>
                                                                        </Grid>
                                                                        <Grid item lg={12} className="tittlegrid">
                                                                            <p className="docTitle">Applicant's CNIC.</p>
                                                                            {applicantCNICBackObjectionRemarks ? (
                                                                                <p className="doc-remarks-inline">{applicantCNICBackObjectionRemarks}</p>
                                                                            ) : null}
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            )}

                                                        {documnets?.paidBillImage && (
                                                            <Grid item lg={2}>
                                                                <Grid
                                                                    container
                                                                    sx={{
                                                                        backgroundColor:
                                                                            flag_paidBillImageFlag !== null
                                                                                ? getDocumentBackGroundColor(
                                                                                    flag_paidBillImageFlag
                                                                                )
                                                                                : getDocumentBackGroundColor(
                                                                                    documnets?.paidBillImageFlag
                                                                                ),
                                                                        borderRadius: "10px",
                                                                        minHeight: "150px",
                                                                        height: "auto",
                                                                    }}
                                                                >
                                                                    <Grid
                                                                        item
                                                                        lg={12}
                                                                        style={{
                                                                            display: "flex",
                                                                            justifyContent: "center",
                                                                            alignItems: "center",
                                                                            paddingBottom: "5px",
                                                                        }}
                                                                    >
                                                                        <div
                                                                            onClick={() => {
                                                                                if (!paidBillImagePopUp) {
                                                                                    setPaidBillImagePopUp(true);
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                display: "flex",
                                                                                justifyContent: "space-between",
                                                                                flexDirection: "column",
                                                                                alignItems: "center",
                                                                            }}
                                                                        >
                                                                            {documnets.paidBillImage?.slice(0, 6) === "data:i" ? (
                                                                                (flag_paidBillImageFlag === 1 || documnets.paidBillImageFlag === 1) ? (
                                                                                    <>
                                                                                        <Base64Image
                                                                                            data={
                                                                                                documnets.paidBillImage
                                                                                            }
                                                                                            width="80"
                                                                                            height="100"
                                                                                        />
                                                                                        <img
                                                                                            src={tickicon}
                                                                                            width="25px"
                                                                                            height="25px"
                                                                                            alt="Tick Icon"
                                                                                            className="doc-verification-status-icon"
                                                                                        />
                                                                                    </>
                                                                                ) : (flag_paidBillImageFlag === -1 || documnets.paidBillImageFlag === -1) ? (
                                                                                    <>
                                                                                        <Base64Image
                                                                                            data={documnets.paidBillImage}
                                                                                            width="80"
                                                                                            height="50"
                                                                                        />
                                                                                        <img
                                                                                            src={crossIcon}
                                                                                            width="25px"
                                                                                            height="25px"
                                                                                            alt="Cross Icon"
                                                                                            className="doc-verification-status-icon"
                                                                                        />
                                                                                    </>
                                                                                ) : (
                                                                                    <Base64Image
                                                                                        data={documnets.paidBillImage}
                                                                                        width="80"
                                                                                        height="50"
                                                                                    />
                                                                                )
                                                                            ) : (
                                                                                <Base64Image data={pdfIcon} width="40" />
                                                                            )}
                                                                            {paidBillImagePopUp && (
                                                                                <OpenBase64ImageInPopup
                                                                                    docVerifyPopUp={paidBillImagePopUp}
                                                                                    hideDocVerifyPopup={hidePropertyDocVerifyAlert}
                                                                                    documentName="Paid Bill Image"
                                                                                    documentImage={documnets.paidBillImage}
                                                                                    width={"100%"}
                                                                                    document={documnets.paidBillImage}
                                                                                    documentFlag={documnets.paidBillImageFlag}
                                                                                    applicationProgressId={dialogRowData.applicationProgressId}
                                                                                    objectionStatus={dialogRowData.objectionStatus}
                                                                                    updateDocFlag={updateDocFlag}
                                                                                    documentRemarks={paidBillImageObjectionRemarks}
                                                                                    titleToUpdateFlag="paidBillImage"
                                                                                    flag_document={flag_paidBillImageFlag}
                                                                                    verificationPermissionService="Part Payment"
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    </Grid>
                                                                    <Grid item lg={12} className="tittlegrid">
                                                                        <p className="docTitle">Paid Bill Image</p>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        )}

                                                    </Grid>
                                                </>
                                            ) : (
                                                <>
                                                    <h3 className="step-title">Documents Verification</h3>
                                                    <div className="description-box description-box--error">
                                                        <p className="group-description">
                                                            <InfoIcon className="icon" /> Document verification is in process by the Customer Service Lead.
                                                        </p>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    ) : dialogRowData.applicationProgressId === 1 &&
                                        dialogRowData.objectionStatus === 1 ? (
                                        <>
                                            <h3 className="step-title">Documents Objection</h3>
                                            <Grid
                                                className="document-verification-container"
                                                container
                                                spacing={1}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "flex-start",
                                                    marginTop: "1%",
                                                    padding: "2%",
                                                }}
                                            >
                                                {(documnets?.applicantCNICFront ||
                                                    documnets?.applicantCNICBack) && (
                                                        <Grid item lg={2}>
                                                            <Grid
                                                                container
                                                                sx={{
                                                                    backgroundColor:
                                                                        flag_applicantCNICFront !== null
                                                                            ? getDocumentBackGroundColor(
                                                                                flag_applicantCNICFront
                                                                            )
                                                                            : getDocumentBackGroundColor(
                                                                                documnets?.applicantCNICFrontFlag
                                                                            ),
                                                                    borderRadius: "10px",
                                                                    minHeight: "150px",
                                                                    height: "auto",
                                                                }}
                                                            >
                                                                <Grid
                                                                    item
                                                                    lg={12}
                                                                    style={{
                                                                        display: "flex",
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                        paddingBottom: "5px",
                                                                    }}
                                                                >
                                                                    <div
                                                                        onClick={() => {
                                                                            if (!copyOfApplicantCNICVerifyPopUp) {
                                                                                setCopyOfApplicantCNICVerifyPopUp(true);
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            display: "flex",
                                                                            justifyContent: "space-between",
                                                                            flexDirection: "column",
                                                                            alignItems: "center",
                                                                        }}
                                                                    >
                                                                        <>
                                                                            <DocumentPreviewThumb
                                                                                src={documnets?.applicantCNICFront}
                                                                            />
                                                                            <DocumentPreviewThumb
                                                                                src={documnets?.applicantCNICBack}
                                                                            />
                                                                            {flag_applicantCNICFront === 1 ? (
                                                                                    <img
                                                                                        src={tickicon}
                                                                                        width="25px"
                                                                                        height="25px"
                                                                                        alt="Tick Icon"
                                                                                        className="doc-verification-status-icon"
                                                                                    />
                                                                            ) : (documnets?.applicantCNICFrontFlag === -1 || flag_applicantCNICFront === -1) ? (
                                                                                    <img
                                                                                        src={crossIcon}
                                                                                        width="25px"
                                                                                        height="25px"
                                                                                        alt="Cross Icon"
                                                                                        className="doc-verification-status-icon"
                                                                                    />
                                                                            ) : null}
                                                                        {copyOfApplicantCNICVerifyPopUp && (
                                                                            <OpenBase64ImageInPopup
                                                                                docVerifyPopUp={copyOfApplicantCNICVerifyPopUp}
                                                                                hideDocVerifyPopup={hideCopyOfApplicantCNICVerifyAlert}
                                                                                documentName="Applicant CNIC"
                                                                                documentImage={documnets.applicantCNICFront}
                                                                                backDocumentImage={documnets.applicantCNICBack}
                                                                                width={"100%"}
                                                                                document={documnets.applicantCNICFront}
                                                                                documentFlag={documnets.applicantCNICFrontFlag}
                                                                                applicationProgressId={dialogRowData.applicationProgressId}
                                                                                objectionStatus={dialogRowData.objectionStatus}
                                                                                updateDocFlag={updateDocFlag}
                                                                                documentRemarks={applicantCNICBackObjectionRemarks}
                                                                                titleToUpdateFlag="copyOfApplicantCNIC"
                                                                                flag_document={flag_applicantCNICFront}
                                                                                flag_documentBack={flag_applicantCNICBack}
                                                                                verificationPermissionService="Part Payment"
                                                                            />
                                                                        )}
                                                                        </>
                                                                    </div>
                                                                </Grid>
                                                                <Grid item lg={12} className="tittlegrid">
                                                                    <p className="docTitle">Applicant's CNIC.</p>
                                                                    {applicantCNICBackObjectionRemarks ? (
                                                                        <p className="doc-remarks-inline">{applicantCNICBackObjectionRemarks}</p>
                                                                    ) : null}
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    )}

                                                {documnets?.paidBillImage && (
                                                    <Grid item lg={2}>
                                                        <Grid
                                                            container
                                                            sx={{
                                                                backgroundColor:
                                                                    flag_paidBillImageFlag !== null
                                                                        ? getDocumentBackGroundColor(flag_paidBillImageFlag)
                                                                        : getDocumentBackGroundColor(documnets.paidBillImageFlag),
                                                                borderRadius: "10px",
                                                                minHeight: "150px",
                                                                height: "auto",
                                                            }}
                                                        >
                                                            <Grid
                                                                item
                                                                lg={12}
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "center",
                                                                    alignItems: "center",
                                                                    paddingBottom: "5px",
                                                                }}
                                                            >
                                                                <div
                                                                    onClick={() => {
                                                                        if (!paidBillImagePopUp) {
                                                                            setPaidBillImagePopUp(true);
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        flexDirection: "column",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    {documnets.paidBillImage?.slice(0, 6) === "data:i" ? (
                                                                        flag_paidBillImageFlag === 1 ? (
                                                                            <>
                                                                                <Base64Image
                                                                                    data={documnets.paidBillImage}
                                                                                    width="80"
                                                                                    height="50"
                                                                                />
                                                                                <img
                                                                                    src={tickicon}
                                                                                    width="25px"
                                                                                    height="25px"
                                                                                    alt="Tick Icon"
                                                                                    className="doc-verification-status-icon"
                                                                                />
                                                                            </>
                                                                        ) : (documnets.paidBillImageFlag === -1 || flag_paidBillImageFlag === -1) ? (
                                                                            <>
                                                                                <Base64Image
                                                                                    data={documnets.paidBillImage}
                                                                                    width="80"
                                                                                    height="50"
                                                                                />
                                                                                <img
                                                                                    src={crossIcon}
                                                                                    width="25px"
                                                                                    height="25px"
                                                                                    alt="Cross Icon"
                                                                                    className="doc-verification-status-icon"
                                                                                />
                                                                            </>
                                                                        ) : (
                                                                            <Base64Image
                                                                                data={documnets.paidBillImage}
                                                                                width="80"
                                                                                height="50"
                                                                            />
                                                                        )
                                                                    ) : (
                                                                        <Base64Image data={pdfIcon} width="40" />
                                                                    )}
                                                                    {paidBillImagePopUp && (
                                                                        <OpenBase64ImageInPopup
                                                                            docVerifyPopUp={paidBillImagePopUp}
                                                                            hideDocVerifyPopup={hidePropertyDocVerifyAlert}
                                                                            documentName="Paid Bill Image"
                                                                            documentImage={documnets.paidBillImage}
                                                                            width={"100%"}
                                                                            document={documnets.paidBillImage}
                                                                            documentFlag={documnets.paidBillImageFlag}
                                                                            applicationProgressId={dialogRowData.applicationProgressId}
                                                                            objectionStatus={dialogRowData.objectionStatus}
                                                                            updateDocFlag={updateDocFlag}
                                                                            documentRemarks={paidBillImageObjectionRemarks}
                                                                            titleToUpdateFlag="paidBillImage"
                                                                            flag_document={flag_paidBillImageFlag}
                                                                            verificationPermissionService="Part Payment"
                                                                        />
                                                                    )}
                                                                </div>
                                                            </Grid>
                                                            <Grid item lg={12} className="tittlegrid">
                                                                <p className="docTitle">Paid Bill Image</p>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </>
                                    ) : dialogRowData.applicationProgressId === 2 &&
                                        (dialogRowData.objectionStatus === 0 ||
                                            dialogRowData.objectionStatus === 2) ? (
                                        <>
                                            <h3 className="step-title">Process Part Payment</h3>
                                            <div className="pp-panel-body">
                                                <div className="pp-payment-fields-grid">
                                                    <div className="pp-payment-field">
                                                        <Typography className="label">Approved Part Payment</Typography>
                                                        <TextField
                                                            type="number"
                                                            value={approvedPayment}
                                                            onChange={(e) => {
                                                                setApprovedPayment(e.target.value);
                                                                if (approvedPaymentError) setApprovedPaymentError('');
                                                            }}
                                                            fullWidth
                                                            size="small"
                                                            placeholder="Enter amount"
                                                            error={!!approvedPaymentError}
                                                            className="pp-payment-input pp-payment-input--editable"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <span style={{ marginRight: '4px', fontWeight: 'bold' }}>
                                                                        Rs.
                                                                    </span>
                                                                )
                                                            }}
                                                        />
                                                        {approvedPaymentError ? (
                                                            <p className="pp-field-error">{approvedPaymentError}</p>
                                                        ) : null}
                                                    </div>
                                                    <div className="pp-payment-field">
                                                        <Typography className="label">Claimed Part Payment</Typography>
                                                        <TextField
                                                            type="text"
                                                            value={
                                                                dialogRowData.partPaymentAmount
                                                                    ? Number(dialogRowData.partPaymentAmount).toLocaleString()
                                                                    : '-'
                                                            }
                                                            disabled
                                                            fullWidth
                                                            size="small"
                                                            className="pp-payment-input"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <span style={{ marginRight: '4px', fontWeight: 'bold' }}>
                                                                        Rs.
                                                                    </span>
                                                                )
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                        </>
                                    ) : dialogRowData.applicationProgressId === 2 &&
                                        (dialogRowData.objectionStatus === 1) ? (
                                        <>
                                            <h3 className="step-title">Claimed Part Payment Objection</h3>
                                            <div className="pp-status-banner pp-status-banner--error">
                                                <CancelIcon />
                                                <span>This application is rejected by sanctioned authority</span>
                                            </div>
                                            <div className="pp-panel-body">
                                                <div className="pp-payment-fields-grid">
                                                    <div className="pp-payment-field">
                                                        <Typography className="label">Claimed Part Payment</Typography>
                                                        <TextField
                                                            type="text"
                                                            value={
                                                                dialogRowData.partPaymentAmount
                                                                    ? Number(dialogRowData.partPaymentAmount).toLocaleString()
                                                                    : '-'
                                                            }
                                                            disabled
                                                            fullWidth
                                                            size="small"
                                                            className="pp-payment-input"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <span style={{ marginRight: '4px', fontWeight: 'bold' }}>
                                                                        Rs.
                                                                    </span>
                                                                )
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="pp-payment-field">
                                                        <Typography className="label">Approved Part Payment</Typography>
                                                        <TextField
                                                            type="text"
                                                            value={
                                                                dialogRowData.approvedPartPayment
                                                                    ? Number(dialogRowData.approvedPartPayment).toLocaleString()
                                                                    : '-'
                                                            }
                                                            disabled
                                                            fullWidth
                                                            size="small"
                                                            className="pp-payment-input"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <span style={{ marginRight: '4px', fontWeight: 'bold' }}>
                                                                        Rs.
                                                                    </span>
                                                                )
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                        </>
                                    ) : dialogRowData.applicationProgressId === 3 &&
                                        (dialogRowData.objectionStatus === 0 ||
                                            dialogRowData.objectionStatus === 2) ? (
                                        <>
                                            <div className="step-title step-title--with-action">
                                                <h4>Application Completed</h4>
                                                <Button
                                                    type="button"
                                                    variant="outlined"
                                                    className="pp-bill-btn"
                                                    onClick={() => downloadBill(dialogRowData.referenceNo)}
                                                >
                                                    <DescriptionIcon sx={{ color: '#006ad1', fontSize: '1rem' }} />
                                                    Bill
                                                </Button>
                                            </div>
                                            <div className="pp-status-banner pp-status-banner--success">
                                                <CheckCircleIcon />
                                                <span>Completed</span>
                                            </div>
                                            <div className="pp-panel-body">
                                                <div className="pp-payment-fields-grid">
                                                    <div className="pp-payment-field">
                                                        <Typography className="label">Approved Part Payment</Typography>
                                                        <TextField
                                                            type="text"
                                                            value={
                                                                dialogRowData.approvedPartPayment
                                                                    ? Number(dialogRowData.approvedPartPayment).toLocaleString()
                                                                    : '-'
                                                            }
                                                            disabled
                                                            fullWidth
                                                            size="small"
                                                            className="pp-payment-input"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <span style={{ marginRight: '4px', fontWeight: 'bold' }}>
                                                                        Rs.
                                                                    </span>
                                                                )
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="pp-payment-field">
                                                        <Typography className="label">Claimed Part Payment</Typography>
                                                        <TextField
                                                            type="text"
                                                            value={
                                                                dialogRowData.partPaymentAmount
                                                                    ? Number(dialogRowData.partPaymentAmount).toLocaleString()
                                                                    : '-'
                                                            }
                                                            disabled
                                                            fullWidth
                                                            size="small"
                                                            className="pp-payment-input"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <span style={{ marginRight: '4px', fontWeight: 'bold' }}>
                                                                        Rs.
                                                                    </span>
                                                                )
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="pp-payment-field">
                                                        <Typography className="label">Current Bill Amount</Typography>
                                                        <TextField
                                                            type="text"
                                                            value={
                                                                dialogRowData.currentBillAmount
                                                                    ? Number(dialogRowData.currentBillAmount).toLocaleString()
                                                                    : '-'
                                                            }
                                                            disabled
                                                            fullWidth
                                                            size="small"
                                                            className="pp-payment-input"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <span style={{ marginRight: '4px', fontWeight: 'bold' }}>
                                                                        Rs.
                                                                    </span>
                                                                )
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="pp-payment-field">
                                                        <Typography className="label">Current Bill Month</Typography>
                                                        <TextField
                                                            type="text"
                                                            value={convertDate(dialogRowData.currentBillMonth) || '-'}
                                                            disabled
                                                            fullWidth
                                                            size="small"
                                                            className="pp-payment-input"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>

                                    ) : null}
                                </div>
                                <div className="actions">
                                    {dialogRowData.applicationProgressId === 1 &&
                                        (dialogRowData.objectionStatus === 0 ||
                                            dialogRowData.objectionStatus === 2) ? (
                                        CheckPermissions(permissions, 'General Services', 'Part Payment', 'Workflow Permissions', 'Document Verification') ?
                                            <>
                                                {(dialogRowData.applicantCNICFrontFlag !== 1 ||
                                                    dialogRowData.applicantCNICBackFlag !== 1 ||
                                                    (documnets?.paidBillImage && documnets?.paidBillImageFlag !== 1)) ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            disabled={!canSubmitPartPaymentDocumentVerification}
                                                            title={
                                                                !canSubmitPartPaymentDocumentVerification
                                                                    ? 'Accept or reject each document before verifying.'
                                                                    : undefined
                                                            }
                                                            onClick={() => updateDocumentStatus()}
                                                            className="view-button"
                                                            style={{
                                                                backgroundColor:
                                                                    (flag_applicantCNICFront === -1 ||
                                                                        flag_applicantCNICBack === -1 || flag_paidBillImageFlag === -1)
                                                                        ? "#E11B3C"
                                                                        : "#003971",
                                                            }}
                                                        >
                                                            {flag_applicantCNICFront === -1 ||
                                                                flag_applicantCNICBack === -1 || flag_paidBillImageFlag === -1
                                                                ? "Reject"
                                                                : "Verify"}
                                                        </button>
                                                    </>
                                                ) : null}
                                            </>
                                            :
                                            null
                                    ) : dialogRowData.applicationProgressId === 1 &&
                                        dialogRowData.objectionStatus === 1 ? (
                                        <>

                                            <button
                                                type="button"
                                                disabled={hasReuploadedDocsPendingVerification}
                                                title={
                                                    hasReuploadedDocsPendingVerification
                                                        ? 'Documents re-uploaded. Pending verification.'
                                                        : undefined
                                                }
                                                onClick={() => {
                                                    if (hasReuploadedDocsPendingVerification) return;
                                                    setOpen(false);
                                                    if (!props.showTable && typeof props.onCloseSearchResults === 'function') {
                                                        props.onCloseSearchResults();
                                                    }
                                                    navigate('/part-payment-update', { state: { dialogRowData } });
                                                }}
                                                className="view-button"
                                            >
                                                Re-Upload Document
                                            </button>

                                        </>
                                    ) : (dialogRowData.applicationProgressId === 2 && dialogRowData.objectionStatus === 0) ? (
                                        canApprove(dialogRowData.partPaymentAmount) ? (
                                            <>
                                                <button
                                                    onClick={() => partPaymentClaim('Approve')}
                                                    className="view-button"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => partPaymentClaim("Reject")}
                                                    className="reject-button"
                                                >
                                                    Reject
                                                </button>
                                            </>
                                        ) : (
                                            // <p style={{ color: '#888', fontSize: '14px', margin: '10px 0' }}>
                                            //     You don't have permission to approve this amount
                                            // </p>
                                            // <div className="description-box">
                                            //     <p className="group-description">
                                            //         <InfoIcon className="icon" />
                                            //         You don't have permission to approve this amount
                                            //     </p>
                                            // </div>
                                            <div className="description-box description-box--error">
                                                <p className="group-description">
                                                    <InfoIcon className="icon" />
                                                    You don&apos;t have permission to approve this amount
                                                </p>
                                            </div>
                                        )
                                    ) : null}
                                </div>
                            </Grid>
                        </Grid>
                    </DialogContentText>
                </DialogContent>
            </Dialog>

            <Dialog
                open={applicationSubittedPopUp}
                onClose={() => setApplicationSubmittedPopUp(false)}
                fullWidth
                maxWidth="md"
                className="wf-submitted-dialog"
                PaperProps={{ className: 'wf-submitted-dialog__paper' }}
            >
                <DialogTitle className="dialog-title">
                    <div className="wf-dialog-header">
                        <Typography className="wf-dialog-title-text">Application Submitted</Typography>
                        <button
                            type="button"
                            className="wf-dialog-close"
                            onClick={() => setApplicationSubmittedPopUp(false)}
                            aria-label="Close dialog"
                        >
                            <CloseIcon id="clearPopup" />
                        </button>
                    </div>
                </DialogTitle>
                <DialogContent className="wf-submitted-dialog__content">
                    <div className="wf-application-details-body">
                        <p className="wf-section-label">Application Details</p>
                        <div className="wf-details-grid">
                            <div className="wf-detail-item">
                                <p className="prop-title">Application Id</p>
                                <p className="prop-detail">{dialogRowData.applicationId || '-'}</p>
                            </div>
                            <div className="wf-detail-item">
                                <p className="prop-title">Applicant Name</p>
                                <p className="prop-detail">{dialogRowData.applicantName || '-'}</p>
                            </div>
                            <div className="wf-detail-item">
                                <p className="prop-title">Current Bill Amount</p>
                                <p className="prop-detail">
                                    {dialogRowData.currentBillAmount
                                        ? Number(dialogRowData.currentBillAmount).toLocaleString()
                                        : '-'}
                                </p>
                            </div>
                            <div className="wf-detail-item">
                                <p className="prop-title">Sub. Through</p>
                                <p className="prop-detail">{dialogRowData.submittedThrough || '-'}</p>
                            </div>
                            <div className="wf-detail-item">
                                <p className="prop-title">Applied Date</p>
                                <p className="prop-detail">{convertDate(dialogRowData.creationDate) || '-'}</p>
                            </div>
                            <div className="wf-detail-item">
                                <p className="prop-title">Subdivision</p>
                                <p className="prop-detail">
                                    {subdivisionName(dialogRowData.subDivisionCode, subDivisions) || '-'}
                                </p>
                            </div>
                            <div className="wf-detail-item">
                                <p className="prop-title">Received From</p>
                                <p className="prop-detail">{dialogRowData.cfcName || '-'}</p>
                            </div>
                            <div className="wf-detail-item wf-detail-item--span3">
                                <p className="prop-title">Address</p>
                                <p className="prop-detail">{dialogRowData.applicantAddress || '-'}</p>
                            </div>
                        </div>
                    </div>
                    {(documnets?.applicantCNICFront || documnets?.applicantCNICBack) ? (
                        <div className="wf-submitted-docs">
                            <p className="wf-section-label">Attached Documents</p>
                            <div className="wf-submitted-docs__grid">
                                {documnets?.applicantCNICFront ? (
                                    <button
                                        type="button"
                                        className="wf-submitted-doc-card"
                                        onClick={() => setSubmittedDocViewer({
                                            open: true,
                                            image: submittedCnicFrontPreview
                                                || normalizeDocumentDataUrl(documnets.applicantCNICFront),
                                            name: 'CNIC Front',
                                        })}
                                    >
                                        <p className="wf-submitted-doc-card__label">CNIC Front</p>
                                        <img
                                            src={submittedCnicFrontPreview || pdfIcon}
                                            alt="CNIC Front"
                                        />
                                    </button>
                                ) : null}
                                {documnets?.applicantCNICBack ? (
                                    <button
                                        type="button"
                                        className="wf-submitted-doc-card"
                                        onClick={() => setSubmittedDocViewer({
                                            open: true,
                                            image: submittedCnicBackPreview
                                                || normalizeDocumentDataUrl(documnets.applicantCNICBack),
                                            name: 'CNIC Back',
                                        })}
                                    >
                                        <p className="wf-submitted-doc-card__label">CNIC Back</p>
                                        <img
                                            src={submittedCnicBackPreview || pdfIcon}
                                            alt="CNIC Back"
                                        />
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </DialogContent>
                <DialogActions className="wf-submitted-dialog__footer">
                    <p className="wf-submitted-dialog__footer-text">
                        Application Submitted by:{' '}
                        {String(dialogRowData.createdBy).length === 13 && /^\d+$/.test(dialogRowData.createdBy)
                            ? dialogRowData.applicantName
                            : dialogRowData.createdBy}{' '}
                        on: {convertDatewithTime(dialogRowData.creationDate)}
                    </p>
                </DialogActions>
                {submittedDocViewer.open ? (
                    <OpenBase64ImageInPopup
                        docVerifyPopUp={submittedDocViewer.open}
                        hideDocVerifyPopup={() => setSubmittedDocViewer({ open: false, image: '', name: '' })}
                        documentName={submittedDocViewer.name}
                        documentImage={submittedDocViewer.image}
                        width="100%"
                        document={submittedDocViewer.image}
                        applicationProgressId={3}
                        objectionStatus={0}
                    />
                ) : null}
            </Dialog>

            <Dialog
                open={actionTakenPopup}
                onClose={() => setActionTakenPopup(false)}
                fullWidth
                maxWidth="md"
                className="wf-action-taken-dialog"
                PaperProps={{ className: 'wf-action-taken-dialog__paper' }}
            >
                <DialogTitle className="dialog-title">
                    <div className="wf-dialog-header">
                        <Typography className="wf-dialog-title-text">
                            {dialogRowData.applicationProgressId === 3
                                ? 'Application Approved'
                                : 'Application Rejected'}
                        </Typography>
                        <button
                            type="button"
                            className="wf-dialog-close"
                            onClick={() => setActionTakenPopup(false)}
                            aria-label="Close dialog"
                        >
                            <CloseIcon id="clearPopup" />
                        </button>
                    </div>
                </DialogTitle>
                <DialogContent className="wf-action-taken-dialog__content">
                    <div className="wf-action-taken-panel">
                        {dialogRowData.applicationProgressId === 3 ? (
                            <>
                                <p className="wf-action-taken-panel__title">Approved Part Payment</p>
                                <TextField
                                    type="text"
                                    value={
                                        dialogRowData.approvedPartPayment
                                            ? Number(dialogRowData.approvedPartPayment).toLocaleString()
                                            : approvedPayment
                                                ? Number(approvedPayment).toLocaleString()
                                                : ''
                                    }
                                    fullWidth
                                    disabled
                                    size="small"
                                    className="wf-action-taken-amount"
                                    InputProps={{
                                        startAdornment: <span style={{ marginRight: 4, fontWeight: 700 }}>Rs.</span>,
                                    }}
                                />
                            </>
                        ) : (
                            <p className="wf-action-taken-reject-msg">
                                The application is rejected by the sanctioned authority.
                            </p>
                        )}
                    </div>
                </DialogContent>
                <DialogActions className="wf-action-taken-dialog__footer">
                    <p className="wf-action-taken-dialog__footer-text">
                        Application{' '}
                        {dialogRowData.applicationProgressId === 3 ? 'Approved' : 'Rejected'} by:{' '}
                        {String(dialogRowData.lastModifiedBy || dialogRowData.createdBy).length === 13
                            && /^\d+$/.test(String(dialogRowData.lastModifiedBy || dialogRowData.createdBy))
                            ? dialogRowData.applicantName
                            : (dialogRowData.lastModifiedBy || dialogRowData.createdBy)}{' '}
                        on: {convertDatewithTime(dialogRowData.lastModificationDate || dialogRowData.creationDate)}
                    </p>
                </DialogActions>
            </Dialog>

            {/* Remarks Dialog */}
            <Dialog
                open={openCancelModal}
                className="pp-remarks-dialog"
                onClose={handleCloseRemarks}
                maxWidth="sm"
                fullWidth
                PaperProps={{ className: 'pp-remarks-dialog__paper' }}
            >
                <DialogTitle className="pp-remarks-dialog__title">
                    <div className="pp-remarks-dialog__header">
                        <Typography className="pp-remarks-dialog__heading">Remarks</Typography>
                        <button
                            type="button"
                            className="pp-remarks-dialog__close"
                            onClick={handleCloseRemarks}
                            aria-label="Close remarks dialog"
                        >
                            <CloseIcon id="clearPopup" />
                        </button>
                    </div>
                </DialogTitle>
                <DialogContent className="pp-remarks-dialog__content">
                    <TextField
                        autoFocus
                        placeholder="Enter remarks for rejection (minimum 10 characters)"
                        type="text"
                        fullWidth
                        variant="outlined"
                        onChange={remarksFormik.handleChange}
                        onBlur={remarksFormik.handleBlur}
                        name="iv_remarks"
                        value={remarksFormik.values.iv_remarks}
                        multiline
                        minRows={5}
                        maxRows={8}
                        className="pp-remarks-dialog__textarea"
                        error={Boolean(remarksFormik.touched.iv_remarks && remarksFormik.errors.iv_remarks)}
                    />
                    {remarksFormik.touched.iv_remarks && remarksFormik.errors.iv_remarks ? (
                        <p className="pp-remarks-dialog__error">{remarksFormik.errors.iv_remarks}</p>
                    ) : null}
                    {error ? (
                        <Error
                            error={error}
                            onClose={() => errorCloses()}
                            backgroundColor="#FFC0CB"
                            textColor="#bb0303"
                            iconColor="#bb0303"
                        />
                    ) : null}
                </DialogContent>
                <DialogActions className="pp-remarks-dialog__actions">
                    <Button
                        type="button"
                        onClick={remarksFormik.handleSubmit}
                        className="pp-remarks-dialog__btn pp-remarks-dialog__btn--confirm"
                    >
                        Confirm
                    </Button>
                    <Button
                        type="button"
                        onClick={handleCloseRemarks}
                        className="pp-remarks-dialog__btn pp-remarks-dialog__btn--cancel"
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(activePpResultDialog)}
                onClose={handleCloseResultPopup}
                className={`dde-result-dialog${resultPopup === 'reject' || resultPopup === 'docsRejected' || apiPopup.type === 'error' ? ' dde-result-dialog--reject' : ''}`}
                fullWidth
                maxWidth="sm"
                PaperProps={{ className: 'dde-result-dialog__paper' }}
            >
                {activePpResultDialog ? (
                    <>
                        <DialogTitle className="dde-result-dialog__title">
                            <Box className="dde-result-dialog__icon-wrap">
                                <activePpResultDialog.Icon className={activePpResultDialog.iconClassName} />
                            </Box>
                            <Typography variant="h6" className="dde-result-dialog__heading">
                                {activePpResultDialog.title}
                            </Typography>
                        </DialogTitle>
                        <DialogContent className="dde-result-dialog__content">
                            <DialogContentText>
                                <p className="dde-result-dialog__message">{activeResultMessage}</p>
                                {activePpResultDialog.showBill ? (
                                    <Box className="dde-result-dialog__actions">
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            className="dde-result-dialog__bill-btn"
                                            onClick={() => downloadBill(dialogRowData.referenceNo)}
                                        >
                                            <DescriptionIcon className="dde-result-dialog__bill-icon" />
                                            Bill
                                        </Button>
                                    </Box>
                                ) : null}
                            </DialogContentText>
                        </DialogContent>
                    </>
                ) : null}
            </Dialog>

            {/* Backdrop */}
            < Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }
                }
                open={viewBackdrop}
            >
                <AppLoader />
            </Backdrop >

        </>
    )
});

// End of module
export default AmendBill2636Table;
